import * as Api from '@ViewModels';
import { THEME } from '@Theme';
import { RichTextEditor } from '@levitate/rich-text';
import { css } from 'aphrodite';
import moment from 'moment';
import { ReactNode, useState } from 'react';
import { invalidateAccountAdminQuery } from '../../../../queries/Admin/Account/useAdminAccountQuery';
import { useAddMeetingNoteMutation } from '../../../../queries/Admin/Account/useAddMeetingNoteMutation';
import { invalidateNoteById } from '../../../../queries/Note/useNoteByIdQuery';
import { Button } from '../../../../web/components-v2/Button';
import { Checkbox } from '../../../../web/components-v2/Checkbox';
import { DateInput } from '../../../../web/components-v2/DateInput';
import { Dialog, DialogPopup, DialogTitle } from '../../../../web/components-v2/Dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../web/components-v2/Select';
import {
	actionItemDraftsToApi,
	type IActionItemDraft,
	MeetingActionItems,
	newActionItemDraft,
} from './MeetingActionItems';
import { meetingNoteComposerStyles as s } from './meetingNoteComposerStyles';
import { buildCcReferencedEntities, NoteCcField } from './NoteCcField';

/** Meeting status options (value === label), matching the legacy MeetingContext panel. */
const MEETING_STATUSES = ['Completed', 'No showed', 'Rescheduled'];
/** Meeting length options in minutes, matching the legacy MeetingContext panel. */
const MEETING_DURATIONS = [15, 30, 45, 60, 90, 120];
/** CSM meeting types, straight off the shared enum (no UI→API transform for CSM). */
const CSM_MEETING_TYPES = Object.values(Api.CsmMeetingTypes);
/** Tag added to `meetingTags` when the onboarding box is checked. */
const ONBOARDING_TAG = 'Onboarding';
const DEFAULT_STATUS = 'Completed';
const DEFAULT_DURATION = 60;
/** Legacy default meeting length (minutes) per CSM meeting type — mirrors the MeetingContext panel. */
const CSM_TYPE_DURATIONS: Partial<Record<Api.CsmMeetingTypes, number>> = {
	[Api.CsmMeetingTypes.AIContentReview]: 30,
	[Api.CsmMeetingTypes.CheckIn]: 45,
	[Api.CsmMeetingTypes.ClientServices]: 30,
	[Api.CsmMeetingTypes.ContentPlanning]: 45,
	[Api.CsmMeetingTypes.CustomContent]: 30,
	[Api.CsmMeetingTypes.CustomerSave]: 30,
	[Api.CsmMeetingTypes.DataRefresh]: 30,
	[Api.CsmMeetingTypes.IntroAndConsult]: 45,
	[Api.CsmMeetingTypes.Onboarding]: 45,
	[Api.CsmMeetingTypes.RenewalCall]: 45,
	[Api.CsmMeetingTypes.SupportCall]: 30,
	[Api.CsmMeetingTypes.TrainingStrategy]: 60,
};

const svgBase = {
	fill: 'none',
	strokeWidth: 2,
	strokeLinecap: 'round' as const,
	strokeLinejoin: 'round' as const,
};

/** Rail-label glyphs (calendar / check-circle / target / clock), tinted with the primary icon color. */
const CalendarGlyph = ({ stroke = THEME.colors.icon.primary }: { stroke?: string }) => (
	<svg width={15} height={15} viewBox='0 0 24 24' stroke={stroke} {...svgBase}>
		<rect x='3' y='4' width='18' height='18' rx='2' />
		<line x1='16' y1='2' x2='16' y2='6' />
		<line x1='8' y1='2' x2='8' y2='6' />
		<line x1='3' y1='10' x2='21' y2='10' />
	</svg>
);
const StatusGlyph = () => (
	<svg width={15} height={15} viewBox='0 0 24 24' stroke={THEME.colors.icon.primary} {...svgBase}>
		<path d='M22 11.08V12a10 10 0 1 1-5.93-9.14' />
		<polyline points='22 4 12 14.01 9 11.01' />
	</svg>
);
const StageGlyph = () => (
	<svg width={15} height={15} viewBox='0 0 24 24' stroke={THEME.colors.icon.primary} {...svgBase}>
		<circle cx='12' cy='12' r='9' />
		<circle cx='12' cy='12' r='3' />
	</svg>
);
const ClockGlyph = () => (
	<svg width={15} height={15} viewBox='0 0 24 24' stroke={THEME.colors.icon.primary} {...svgBase}>
		<circle cx='12' cy='12' r='9' />
		<polyline points='12 7 12 12 15 14' />
	</svg>
);
/** Footer "Add action item" glyph — a checklist tinted with the success color. */
const ActionItemGlyph = () => (
	<svg width={18} height={18} viewBox='0 0 24 24' stroke={THEME.colors.success.dark} {...svgBase}>
		<path d='M9 11l3 3L22 4' />
		<path d='M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11' />
	</svg>
);

export interface IMeetingNoteComposerProps {
	/** The account the meeting is logged against — supplies id, current stage, and the CSM note id. */
	account: Api.IAccount;
	/** Whether the dialog is shown. */
	isOpen: boolean;
	/** Called when the dialog requests close (save or cancel). */
	onClose: () => void;
}

/** A labeled meeting-metadata field: a label (glyph + text) on top, control below. */
const LabeledField = ({ label, icon, children }: { label: string; icon: ReactNode; children: ReactNode }) => (
	<div className={css(s.field)}>
		<span className={css(s.label)}>
			{icon}
			{label}
		</span>
		{children}
	</div>
);

/**
 * Account Overview "Add Meeting Notes" composer — no MobX. Implements the two-pane "Add Meeting
 * Notes" design: the shared TipTap editor + CC + action items on the left (with a Save / Next
 * Meeting / Add-action-item footer) and the meeting metadata (type / status / stage / length /
 * onboarding) on the right rail. Logs the note through {@link useAddMeetingNoteMutation}
 * (`POST admin/account/{id}/meeting`, with follow-up stage / next-meeting PUTs when they change),
 * invalidates the CSM note + account queries so the read display refetches, then closes.
 */
export const MeetingNoteComposer = ({ account, isOpen, onClose }: IMeetingNoteComposerProps) => {
	const currentStage = account.additionalInfo?.stage ?? '';
	// Seed in UTC to match the read display + legacy (nextMeeting is a UTC-midnight date); local
	// parsing would roll it back a day and re-save the shift.
	const currentNextMeeting = account.additionalInfo?.nextMeeting
		? moment.utc(account.additionalInfo.nextMeeting).format('YYYY-MM-DD')
		: '';
	const [meetingType, setMeetingType] = useState('');
	const [meetingStatus, setMeetingStatus] = useState(DEFAULT_STATUS);
	const [durationInMinutes, setDurationInMinutes] = useState(DEFAULT_DURATION);
	const [onboarding, setOnboarding] = useState(false);
	const [accountStage, setAccountStage] = useState(currentStage);
	const [nextMeeting, setNextMeeting] = useState(currentNextMeeting);
	const [showNextMeeting, setShowNextMeeting] = useState(false);
	const [actionItems, setActionItems] = useState<IActionItemDraft[]>([]);
	const [ccUsers, setCcUsers] = useState<Api.IUser[]>([]);
	const [html, setHtml] = useState('');
	const addMeetingNote = useAddMeetingNoteMutation();

	const nextMeetingLabel = nextMeeting
		? `Next Meeting: ${moment(nextMeeting).format('MMM D, YYYY')}`
		: 'Next Meeting: Not Set!';

	const onSave = async () => {
		const content = Api.createRawRichTextContentStateWithHtmlStringValue(html);
		const meetingTags = onboarding ? [ONBOARDING_TAG] : [];
		// Only round-trip the stage / next-meeting PUTs when those values actually changed.
		const updatedStage = accountStage && accountStage !== currentStage ? accountStage : undefined;
		const updatedNextMeeting = nextMeeting && nextMeeting !== currentNextMeeting ? nextMeeting : undefined;
		const actionItemsPayload = actionItemDraftsToApi(actionItems);

		const saved = await addMeetingNote.mutateAsync({
			accountId: account.id,
			meetingType,
			meetingStatus,
			durationInMinutes,
			meetingTags,
			accountStage: accountStage || undefined,
			content,
			actionItems: actionItemsPayload.length ? actionItemsPayload : undefined,
			referencedEntities: buildCcReferencedEntities(ccUsers),
			updatedStage,
			updatedNextMeeting,
		});
		if (saved) {
			invalidateNoteById(account.additionalInfo?.csmNoteId);
			invalidateAccountAdminQuery();
		}
		onClose();
	};

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && onClose()} dismissOnOutsideClick={false}>
			<DialogPopup styles={[s.popup]}>
				<div className={css(s.header)}>
					<DialogTitle styles={[s.title]}>Add Meeting Notes</DialogTitle>
				</div>

				<div className={css(s.grid)}>
					<div className={css(s.left)}>
						<div className={css(s.leftScroll)}>
							<NoteCcField ccUsers={ccUsers} onChange={setCcUsers} />
							<RichTextEditor
								initialContent=''
								placeholder='Write your note…'
								showLink
								onChange={nextHtml => setHtml(nextHtml)}
							/>
							{actionItems.length > 0 && (
								<MeetingActionItems items={actionItems} onChange={setActionItems} hideAddButton />
							)}
						</div>

						<div className={css(s.leftFooter)}>
							<div className={css(s.footerRow)}>
								<Button onClick={onSave} isLoading={addMeetingNote.isPending} disabled={!meetingType}>
									Save
								</Button>
								<Button variant='link' styles={[s.footLink]} onClick={() => setShowNextMeeting(open => !open)}>
									<CalendarGlyph stroke={THEME.colors.warning.dark} />
									{nextMeetingLabel}
								</Button>
								<Button
									variant='link'
									styles={[s.footLink]}
									onClick={() => setActionItems(items => [...items, newActionItemDraft(items)])}
								>
									<ActionItemGlyph />
									Add action item
								</Button>
							</div>
							{showNextMeeting && <DateInput value={nextMeeting} onChange={value => setNextMeeting(value)} />}
						</div>
					</div>

					<div className={css(s.sidebar)}>
						<span className={css(s.notePill)}>CSM Note</span>

						<LabeledField label='Meeting Type' icon={<CalendarGlyph />}>
							<Select
								value={meetingType}
								onValueChange={type => {
									setMeetingType(type);
									// Legacy parity: picking a type resets the length to that type's default.
									setDurationInMinutes(CSM_TYPE_DURATIONS[type as Api.CsmMeetingTypes] ?? DEFAULT_DURATION);
								}}
							>
								<SelectTrigger styles={s.fullControl}>
									<SelectValue placeholder='Select a meeting type' />
								</SelectTrigger>
								<SelectContent>
									{CSM_MEETING_TYPES.map(type => (
										<SelectItem key={type} value={type}>
											{type}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<label className={css(s.onboardingRow)}>
								<Checkbox checked={onboarding} onCheckedChange={checked => setOnboarding(checked === true)} />
								<span>This was part of the onboarding process</span>
							</label>
						</LabeledField>

						<LabeledField label='Meeting Status' icon={<StatusGlyph />}>
							<Select value={meetingStatus} onValueChange={setMeetingStatus}>
								<SelectTrigger styles={s.fullControl}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{MEETING_STATUSES.map(status => (
										<SelectItem key={status} value={status}>
											{status}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</LabeledField>

						<LabeledField label='Account Stage' icon={<StageGlyph />}>
							<Select value={accountStage} onValueChange={setAccountStage}>
								<SelectTrigger styles={s.fullControl}>
									<SelectValue placeholder='Select a stage' />
								</SelectTrigger>
								<SelectContent>
									{Api.NormalizedStages.map(stage => (
										<SelectItem key={stage} value={stage}>
											{stage}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</LabeledField>

						<LabeledField label='Meeting Length' icon={<ClockGlyph />}>
							<Select value={String(durationInMinutes)} onValueChange={value => setDurationInMinutes(Number(value))}>
								<SelectTrigger styles={s.fullControl}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{MEETING_DURATIONS.map(minutes => (
										<SelectItem key={minutes} value={String(minutes)}>
											{minutes} minutes
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</LabeledField>
					</div>
				</div>
			</DialogPopup>
		</Dialog>
	);
};
